import React, { useEffect } from 'react';
import Navigation from '../components/Navigation.jsx';
import Footer from '../components/Footer.jsx';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver.jsx';
function ThankYouPage() {
  // Intersection observers for animations
  const [heroRef, heroVisible] = useIntersectionObserver({
    threshold: 0.2
  });
  const [section1Ref, section1Visible] = useIntersectionObserver({
    threshold: 0.2
  });
  const [section2Ref, section2Visible] = useIntersectionObserver({
    threshold: 0.2
  });
  const [section3Ref, section3Visible] = useIntersectionObserver({
    threshold: 0.2
  });
  const [section4Ref, section4Visible] = useIntersectionObserver({
    threshold: 0.2
  });
  useEffect(() => {
    window.scrollTo(0, 0);

    // Set page title and meta tags
    if (typeof document !== 'undefined') {
      document.title = 'Thank You – People-Pleasing in the Therapy Room | Hopeful Paths';

      // Update or create meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.name = 'description';
        document.head.appendChild(metaDescription);
      }
      metaDescription.content = "Your free guide is on its way. Take a breath, settle in, and explore a community where you don't have to perform safety to be accepted.";
    }
  }, []);
  return <>
      <Navigation />

      {/* Hero Section */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 md:py-28 bg-gradient-to-br from-warm-50 to-sage-50">
        <div ref={heroRef} className={`max-w-3xl mx-auto text-center transition-all duration-700 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-light text-sage-900 mb-6 leading-tight">
            You're Not Alone — And You're Not Doing It Wrong.
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 leading-relaxed mb-4">
            "People-Pleasing in the Therapy Room" is on its way to your inbox.
          </p>
          <p className="text-lg md:text-xl text-gray-600 italic">
            I invite you to pause and take one belly breath before you read on.
          </p>
        </div>
      </section>

      {/* Section 1: Before You Dive In */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 md:py-24 bg-white">
        <div ref={section1Ref} className={`max-w-2xl mx-auto transition-all duration-700 ${section1Visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-3xl md:text-4xl font-serif font-light text-sage-900 mb-6">
            Before You Dive In
          </h2>
          <h3 className="text-xl md:text-2xl font-medium text-sage-800 mb-6">
            Here is a gentle invitation from me:
          </h3>
          <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
            <p>Before you open the guide, pause for a moment.</p>
            <p>Notice how your body feels right now—your shoulders, your breath, your jaw.</p>
            <p>This isn't just information you're about to read. It's a reflection of your lived experience—the small, wise ways your body has protected you for years.</p>
            <p>Let this guide land slowly. It's not something to get through. It's something to feel.</p>
          </div>
        </div>
      </section>

      {/* Section 2: What You'll Find Inside */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 md:py-24 bg-gradient-to-br from-sage-50 to-warm-50">
        <div ref={section2Ref} className={`max-w-2xl mx-auto transition-all duration-700 ${section2Visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-3xl md:text-4xl font-serif font-light text-sage-900 mb-6">
            What You'll Find Inside
          </h2>
          <h3 className="text-xl md:text-2xl font-medium text-sage-800 mb-6">
            You'll explore…
          </h3>
          <ul className="space-y-4 text-lg text-gray-700 leading-relaxed mb-8">
            <li className="flex items-start">
              <span className="text-warm-600 mr-3 mt-1">•</span>
              <span>Why therapy can sometimes recreate the same dynamics we came to heal from</span>
            </li>
            <li className="flex items-start">
              <span className="text-warm-600 mr-3 mt-1">•</span>
              <span>How identity and power shape what safety feels like in the room</span>
            </li>
            <li className="flex items-start">
              <span className="text-warm-600 mr-3 mt-1">•</span>
              <span>What fawning looks like—and how to notice it without shame</span>
            </li>
            <li className="flex items-start">
              <span className="text-warm-600 mr-3 mt-1">•</span>
              <span>Practices for bringing honesty and authenticity back into your healing relationships</span>
            </li>
          </ul>
          
          {/* Pull Quote */}
          <div className="bg-white border-l-4 border-warm-500 p-6 md:p-8 rounded-r-lg shadow-md">
            <p className="text-xl md:text-2xl font-serif italic text-sage-800 leading-relaxed">
              "These aren't personal failings—they're intelligent survival strategies your body created to keep you safe."
            </p>
          </div>
        </div>
      </section>

      {/* Section 3: When You're Ready for Deeper Support */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 md:py-24 bg-white">
        <div ref={section3Ref} className={`max-w-2xl mx-auto transition-all duration-700 ${section3Visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-3xl md:text-4xl font-serif font-light text-sage-900 mb-8">
            When You're Ready for Deeper Support
          </h2>
          <div className="space-y-4 text-lg text-gray-700 leading-relaxed mb-8">
            <p>The work of unlearning people-pleasing—especially in therapy—is brave, subtle, and tender.</p>
            <p>If reading this guide stirs something in you, I invite you to consider joining a community of motivated women who are practicing to claim their space at their own pace. This is a free, no-cost service I offer.</p>
            <ul className="space-y-3 my-6 pl-4">
              <li className="flex items-start">
                <span className="text-warm-600 mr-3 mt-1">•</span>
                <span><strong>Join my free Facebook support group:</strong> People-Pleasing No More — a space for women who are ready to explore new ways of being real and supported.</span>
              </li>
            </ul>
            <p>You need to feel seen. And you don't have to do this alone.</p>
          </div>

          {/* CTA Button */}
          <div className="text-center mt-10">
            <a href="https://www.facebook.com/groups/738372145079502" target="_blank" rel="noopener noreferrer" className="inline-block bg-warm-500 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-warm-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5" data-tracking-id="link-5e1f8efd0461c952">
              Join My Free Facebook Support Group
            </a>
          </div>
        </div>
      </section>

      {/* Section 4: Closing Note */}
      <section className="px-4 sm:px-6 lg:px-8 py-20 md:py-24 bg-gradient-to-br from-warm-50 to-sage-50">
        <div ref={section4Ref} className={`max-w-2xl mx-auto transition-all duration-700 ${section4Visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-3xl md:text-4xl font-serif font-light text-sage-900 mb-8">
            Closing Note
          </h2>
          <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
            <p>Healing happens in the quiet moments—when you finally feel seen for what you've been carrying.</p>
            <p>Thank you for being here. I'm honored to walk alongside you, even in this small way.</p>
            <p className="text-base text-gray-600 italic mt-8 pt-6 border-t border-sage-200">
              If you don't see the email within a few minutes, check your promotions or spam folder—the email with the free guide will come from yphilipos@gmail.com
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </>;
}
export default ThankYouPage;